import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { useGetEventTraces, useGetEventValues, useLazyQuerySimplePipelinesByCluster } from '@ion/api';

import buildPipelineNamesByWritekey from './buildPipelineNamesByWritekey';
import buildOmitArgs from './buildOmitArgs';
import collectPayloadPaths from './collectPayloadPaths';
import filterEventsByPayload from './filterEventsByPayload';
import isPacketSizeTooLarge from './isPacketSizeTooLarge';
import FILTER_KEYS from './FILTER_KEYS';

const DEFAULT_EVENTS_REQUESTED = '30';

const DEFAULT_FILTERS = {
  [FILTER_KEYS.INTEGRATION_IDS]: [],
  [FILTER_KEYS.WRITEKEYS]: [],
  [FILTER_KEYS.EVENT_IDS]: [],
  [FILTER_KEYS.EVENT_NAMES]: [],
  [FILTER_KEYS.STATUSES]: [],
};

const EventDebuggerTableContext = createContext();

function useEventDebuggerTableContext() {
  return useContext(EventDebuggerTableContext);
}

function EventDebuggerTableContextProvider({ children, initialState }) {
  const [getPipelineMetaDataByCluster, { pipelines, loading: pipelinesLoading }] =
    useLazyQuerySimplePipelinesByCluster();
  const [getEventTraces, { events, loading: eventTracesLoading, error: eventTracesError }] = useGetEventTraces();
  const [getEventValues, { eventValues, loading: eventValuesLoading }] = useGetEventValues();
  const [cluster, setCluster] = useState(initialState?.clusterId);
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS, ...initialState?.filters });
  const [eventsRequested, setEventsRequested] = useState(DEFAULT_EVENTS_REQUESTED);
  const [payloadConditions, setPayloadConditions] = useState([]);

  const { requestOmitted, omitArgs } = buildOmitArgs(eventsRequested);

  // Stable reference so the memos below don't recompute every render when there are no events.
  const fetchedEvents = useMemo(() => events ?? [], [events]);

  // Field-path autocomplete hints, harvested from the fetched input payloads.
  const payloadPaths = useMemo(() => collectPayloadPaths(fetchedEvents), [fetchedEvents]);

  // Filtered once here (memoized to match payloadPaths) so every consumer — the table and the drawer
  // prev/next nav — sees the same list, and the filter loop doesn't re-run on unrelated renders.
  const filteredEvents = useMemo(
    () => filterEventsByPayload({ events: fetchedEvents, conditions: payloadConditions }),
    [fetchedEvents, payloadConditions]
  );

  const updateFilter = filter => {
    setFilters(currentFilters => ({
      ...currentFilters,
      ...filter,
    }));
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPayloadConditions([]);
  };

  const onRefresh = () => {
    if (cluster) {
      getEventTraces({ clusterId: cluster, perPage: eventsRequested, ...filters, ...omitArgs });
    }
  };

  const fetchCluster = (clusterId, clusterFilters) => {
    getPipelineMetaDataByCluster(clusterId);
    getEventValues({ clusterId });
    getEventTraces({ clusterId, perPage: eventsRequested, ...clusterFilters, ...omitArgs });
  };

  // A deep link arrives with its cluster already selected, so the cluster select never calls
  // onClusterChange (which would clear the linked filters). Fetch it here instead, once.
  useEffect(() => {
    if (initialState) {
      fetchCluster(initialState.clusterId, filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClusterChange = selectedCluster => {
    setCluster(selectedCluster);
    clearFilters();
    fetchCluster(selectedCluster);
  };

  const onEventsRequestedChange = newEventsRequested => {
    setEventsRequested(newEventsRequested);
  };

  return (
    <EventDebuggerTableContext.Provider
      value={{
        cluster,
        pipelinesByWritekey: buildPipelineNamesByWritekey(pipelines),
        filterOptions: eventValues,
        filterOptionsLoading: pipelinesLoading || eventValuesLoading,
        filters,
        updateFilter,
        clearFilters,
        onRefresh,
        onClusterChange,
        eventsLoading: pipelinesLoading || eventTracesLoading,
        events: filteredEvents,
        fetchedEventsCount: fetchedEvents.length,
        packetSizeTooLarge: isPacketSizeTooLarge(eventTracesError),
        eventsRequested,
        onEventsRequestedChange,
        requestOmitted,
        payloadConditions,
        setPayloadConditions,
        payloadPaths,
      }}
    >
      {children}
    </EventDebuggerTableContext.Provider>
  );
}

EventDebuggerTableContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialState: PropTypes.shape({
    clusterId: PropTypes.string.isRequired,
    filters: PropTypes.object.isRequired,
  }),
};

export { EventDebuggerTableContextProvider, useEventDebuggerTableContext };
